import { styled } from '@mui/material/styles';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { semantic } from '../../../theme/tokens/semantic';

/**
 * Page-header / hero-treatment icon container. A 56×56 white circle with
 * a soft drop shadow, centered children. Used for the centered icon above
 * a page title (e.g. the gradient sparkle on the AI Agents page).
 *
 * Distinct from `VyIconTile`:
 *  - `VyIconTile` is a 36×36 tinted square — used as inline icon badges
 *    inside cards, with a tile color prop driving the icon glyph color.
 *  - `VyHeroIcon` is a 56×56 white circle — used as a page-header beacon,
 *    with no opinion on the icon's color (the consumer passes a gradient
 *    or themed icon component directly).
 *
 * Pair with any icon component from the lib (e.g. `<AiSparklesGradient />`).
 * The icon is rendered at 32px nominal size — matches the Figma spec.
 */
export interface VyHeroIconProps extends HTMLAttributes<HTMLDivElement> {
  /** Icon element rendered at the center. Author it at 32×32 for the right scale. */
  children: ReactNode;
}

const HeroIconRoot = styled('div')(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  // Same elevation as the default VyCard surface — gentle floating feel
  // against the page bg.
  boxShadow: semantic.shadow.base,
}));

export const VyHeroIcon = forwardRef<HTMLDivElement, VyHeroIconProps>(
  function VyHeroIcon({ children, ...rest }, ref) {
    return (
      <HeroIconRoot ref={ref} {...rest}>
        {children}
      </HeroIconRoot>
    );
  },
);
